/* ─── Collection infinite scroll ─────────────────────────────────────────────
   Progressive enhancement over the numbered pagination on collection pages.

   Markup contract (see sections/collection-template.liquid):
   - #Collection[data-infinite-scroll]  — the container; also carries
       data-next-url (next page URL), data-load-more-text, data-loading-text.
   - [data-products-list]               — the <ul> that receives appended <li>s.
   - [data-pagination-wrapper]          — the numbered pagination (hidden when JS
       takes over, restored as a fallback if a fetch fails).

   New pages are fetched, their product <li>s appended, and the address bar is
   kept in sync so refresh / back returns near the same spot. Card colour
   swatches (<card-color-swatches>) upgrade automatically on append; lazysizes
   picks up new images via its own MutationObserver.

   Return-to-collection: before the shopper leaves (e.g. to a product page) the
   loaded product list and scroll position are saved to sessionStorage, and
   restored when they come back — so all previously loaded products and the exact
   scroll position reappear, instead of only the last page the URL pointed at.
   The scroll position is anchored to a product card (not an absolute pixel
   offset) and re-applied as async widgets settle, so it stays accurate even
   deep into the list; auto-load is paused until the position settles. The
   Restockify "Notify me" portals are stripped from the saved/restored HTML so
   the app re-adds a single one per card instead of duplicating on refresh.
   ─────────────────────────────────────────────────────────────────────────── */

(function () {
  // Take scroll restoration away from the browser. On a back/forward navigation
  // the browser otherwise restores scroll asynchronously — after our own restore
  // has run — snapping to the top of the freshly injected list before we can
  // reposition, which shows as a flash of the collection's start on deep pages.
  // With 'manual' we are the sole authority: the JS path repositions on restore,
  // and the back/forward-cache path repositions in the pageshow handler below.
  if ('scrollRestoration' in window.history) {
    try { window.history.scrollRestoration = 'manual'; } catch (e) {}
  }

  function snapshotKey() {
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete('page');
      return 'infinite-scroll:' + url.pathname + url.search;
    } catch (e) {
      return 'infinite-scroll:' + window.location.pathname;
    }
  }

  function readSnapshot() {
    try {
      var raw = sessionStorage.getItem(snapshotKey());
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  // Reveal the product grid that the section's inline script hid while a restore
  // was pending (see collection-template.liquid). Called once we've repositioned
  // so the server-rendered last-loaded page never flashes under the header.
  function reveal() {
    try {
      document.documentElement.classList.remove('collection-restoring');
    } catch (e) {}
  }

  // Scroll so the anchored product card sits where it did when the snapshot was
  // taken. Recomputed against live layout, so it stays correct however card
  // heights have shifted (Judge.me badges, images) since the snapshot.
  function scrollToAnchor(list, data) {
    if (!data) return;
    var idx = typeof data.anchorIndex === 'number' ? data.anchorIndex : -1;
    var kids = list.children;
    if (idx >= 0 && idx < kids.length) {
      var top = kids[idx].getBoundingClientRect().top +
        (window.scrollY || window.pageYOffset || 0);
      window.scrollTo(0, Math.max(0, Math.round(top + (data.anchorDelta || 0))));
    } else {
      window.scrollTo(0, data.scrollY || 0);
    }
  }

  function init(container) {
    if (!container || container.dataset.infiniteScrollInit) return;

    var list = container.querySelector('[data-products-list]');
    if (!list) return;
    container.dataset.infiniteScrollInit = 'true';

    var loadMoreText = container.dataset.loadMoreText || 'Load more';
    var loadingText = container.dataset.loadingText || 'Loading…';
    var paginationWrapper = container.querySelector('[data-pagination-wrapper]');

    // JS takes over navigation — hide the numbered pagination.
    if (paginationWrapper) paginationWrapper.style.display = 'none';

    var trigger = document.createElement('div');
    trigger.className = 'infinite-scroll__trigger text-center';
    trigger.style.margin = '30px 0';

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn--tertiary infinite-scroll__button';
    button.textContent = loadMoreText;
    trigger.appendChild(button);
    container.appendChild(trigger);

    var loading = false;
    var observer = null;
    // True while a restored snapshot is settling its scroll position. Auto-load
    // is suppressed during this window so the observer can't fire against a
    // transient (mid-restore) scroll position and pull in spurious pages.
    var restoring = false;

    // ── Return-to-collection snapshot ──────────────────────────────────────
    // When the shopper opens a product and comes back, the browser re-renders
    // only the page it left on (e.g. ?page=4), losing pages 1–3 and the scroll
    // position. We save the loaded list + scroll offset before leaving and
    // restore them on the way back. Keyed by collection + filters (ignoring the
    // page number) so the same view is matched regardless of which page it was on.
    // See snapshotKey()/scrollToAnchor() at module scope for the shared helpers.

    // Elements injected client-side by the Restockify back-in-stock app
    // (<div class="nm-portal"> wrapping the "Notify me" button). The app appends
    // one on every DOM pass without checking for an existing one, so if a saved
    // snapshot already contains them they stack up — one extra per restore. We
    // strip them out on both save and restore; Restockify then re-adds exactly
    // one per card via its own DOM watcher.
    var APP_INJECTED_SELECTOR = '.nm-portal';

    function stripInjected(root) {
      var nodes = root.querySelectorAll(APP_INJECTED_SELECTOR);
      Array.prototype.forEach.call(nodes, function (node) { node.remove(); });
    }

    // Anchor the saved scroll position to a specific product card rather than an
    // absolute pixel offset. Deep into the list (page 40+) the per-card Judge.me
    // rating badges render asynchronously and grow hundreds of cards' heights
    // *after* restore, which throws an absolute offset off by many pages. An
    // element index + intra-card delta is recomputed against live layout on
    // restore, so it lands on the same product no matter how heights shift.
    function computeAnchor() {
      var y = window.scrollY || window.pageYOffset || 0;
      var kids = list.children;
      for (var i = 0; i < kids.length; i++) {
        var rect = kids[i].getBoundingClientRect();
        var top = rect.top + y;
        // First card whose bottom edge is at or below the viewport top.
        if (top + rect.height > y) {
          return { index: i, delta: y - top };
        }
      }
      return { index: -1, delta: 0 };
    }

    function saveSnapshot() {
      try {
        var clone = list.cloneNode(true);
        stripInjected(clone);
        var anchor = computeAnchor();
        sessionStorage.setItem(
          snapshotKey(),
          JSON.stringify({
            html: clone.innerHTML,
            nextUrl: container.dataset.nextUrl || '',
            scrollY: window.scrollY || window.pageYOffset || 0,
            anchorIndex: anchor.index,
            anchorDelta: anchor.delta,
            url: window.location.href
          })
        );
      } catch (e) {
        // Quota exceeded or storage unavailable — restore just won't happen.
      }
    }

    function restoreSnapshot() {
      // Only restore when coming back (or reloading) — not on a fresh visit,
      // so shared/bookmarked ?page=N links still behave normally.
      var navType = '';
      try {
        var entry = performance.getEntriesByType('navigation')[0];
        navType = entry ? entry.type : '';
      } catch (e) {}
      if (navType !== 'back_forward' && navType !== 'reload') return false;

      var data = readSnapshot();
      if (!data || !data.html) return false;

      list.innerHTML = data.html;
      // Belt-and-braces: drop any Restockify portals carried over from an older
      // snapshot saved before this fix shipped. This runs synchronously, before
      // the app's MutationObserver microtask fires, so it sees clean cards and
      // injects a single "Notify me" button per sold-out product.
      stripInjected(list);
      if (data.nextUrl) {
        container.dataset.nextUrl = data.nextUrl;
      } else {
        delete container.dataset.nextUrl;
      }
      if (data.url) {
        try { window.history.replaceState(null, '', data.url); } catch (e) {}
      }

      // Restore scroll to the anchored product card, re-applying as async
      // content (Judge.me badges, images) settles and shifts card heights.
      function applyScroll() { scrollToAnchor(list, data); }

      restoring = true;
      var settleTimers = [];
      function endRestore() {
        if (!restoring) return;
        restoring = false;
        settleTimers.forEach(function (t) { clearTimeout(t); });
        window.removeEventListener('load', applyScroll);
        ['wheel', 'touchmove', 'keydown'].forEach(function (ev) {
          window.removeEventListener(ev, onUserScroll);
        });
        // Hand back to the observer now that the position is stable.
        updateVisibility();
        if (observer && hasNext()) {
          observer.unobserve(trigger);
          observer.observe(trigger);
        }
      }
      // A real user gesture ends the managed restore immediately — never fight
      // the shopper for control of the scroll position.
      function onUserScroll() { endRestore(); }

      // Position synchronously, in the same task that injected the HTML, so the
      // first paint already shows the anchored spot — no flash of the list top.
      // (getBoundingClientRect forces the layout this needs; card image boxes
      // have fixed aspect ratios, so heights are known before images load.)
      applyScroll();
      requestAnimationFrame(function () {
        applyScroll();
        requestAnimationFrame(applyScroll);
      });
      // Re-apply as late-rendering widgets change heights, then give up control.
      [150, 400, 800, 1500].forEach(function (ms) {
        settleTimers.push(setTimeout(applyScroll, ms));
      });
      settleTimers.push(setTimeout(endRestore, 2000));
      window.addEventListener('load', applyScroll);
      ['wheel', 'touchmove', 'keydown'].forEach(function (ev) {
        window.addEventListener(ev, onUserScroll, { passive: true });
      });
      return true;
    }

    // pagehide (not beforeunload) so the browser's back/forward cache stays
    // eligible; it also fires when the page is frozen into that cache.
    window.addEventListener('pagehide', saveSnapshot);

    restoreSnapshot();

    function hasNext() {
      return !!container.dataset.nextUrl;
    }

    function updateVisibility() {
      if (hasNext()) {
        trigger.style.display = '';
      } else {
        trigger.style.display = 'none';
        if (observer) observer.disconnect();
      }
    }

    function loadNext() {
      if (loading || !hasNext()) return;
      var nextUrl = container.dataset.nextUrl;
      loading = true;
      button.disabled = true;
      button.textContent = loadingText;

      fetch(nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newContainer = doc.getElementById('Collection');
          var newList = newContainer && newContainer.querySelector('[data-products-list]');

          if (newList) {
            var items = newList.children;
            // Live HTMLCollection shrinks as nodes move, so snapshot first.
            Array.prototype.slice.call(items).forEach(function (li) {
              list.appendChild(document.importNode(li, true));
            });
          }

          // Advance / clear the cursor from the freshly fetched page.
          var next = newContainer && newContainer.dataset.nextUrl;
          if (next) {
            container.dataset.nextUrl = next;
          } else {
            delete container.dataset.nextUrl;
          }

          // Keep the address bar on the page we just loaded.
          try {
            window.history.replaceState(null, '', nextUrl);
          } catch (e) {}
        })
        .catch(function () {
          // Fetch failed — fall back to the numbered pagination so the shopper
          // is never stranded, and stop auto-loading.
          if (paginationWrapper) paginationWrapper.style.display = '';
          if (observer) observer.disconnect();
          container.dataset.infiniteScrollFailed = 'true';
        })
        .then(function () {
          loading = false;
          button.disabled = false;
          button.textContent = loadMoreText;
          if (container.dataset.infiniteScrollFailed) {
            trigger.style.display = 'none';
            return;
          }
          updateVisibility();
          // Re-arm the observer so consecutive short pages keep filling the
          // viewport — an element already intersecting won't re-fire otherwise.
          if (observer && hasNext()) {
            observer.unobserve(trigger);
            observer.observe(trigger);
          }
        });
    }

    button.addEventListener('click', loadNext);

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        function (entries) {
          if (restoring) return;
          entries.forEach(function (entry) {
            if (entry.isIntersecting) loadNext();
          });
        },
        { rootMargin: '400px 0px' }
      );
      observer.observe(trigger);
    }

    updateVisibility();
  }

  function boot() {
    var containers = document.querySelectorAll('[data-infinite-scroll]');
    Array.prototype.forEach.call(containers, init);
    // init() has synchronously injected the saved list and set the scroll; safe
    // to show the grid now (in the same task, before the browser paints).
    reveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Back/forward-cache restore: the frozen page (DOM + heights) comes back
  // intact and init does not re-run, but with manual scroll restoration the
  // browser no longer repositions us — so anchor the scroll ourselves. The DOM
  // is already fully laid out here, so a single pass (plus one rAF for safety)
  // lands exactly, with no flash.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    var data = readSnapshot();
    if (!data) return;
    var containers = document.querySelectorAll('[data-infinite-scroll]');
    Array.prototype.forEach.call(containers, function (container) {
      var list = container.querySelector('[data-products-list]');
      if (!list) return;
      scrollToAnchor(list, data);
      requestAnimationFrame(function () { scrollToAnchor(list, data); });
    });
  });

  // Theme editor: re-init when the collection section is reloaded.
  document.addEventListener('shopify:section:load', function (event) {
    var container = event.target.querySelector('[data-infinite-scroll]');
    if (container) init(container);
  });
})();
